import type { MinigameId } from '@minigame-race/contracts';
import { useEffect } from 'react';
import { MINIGAME_META } from '../race/registry.js';
import { Icon } from './Icon.js';
import { color } from '../theme.js';

/**
 * The "?" affordance and the rules dialog it opens.
 *
 * Deliberately NOT a pause. The segment clock keeps running while this is open,
 * because the clock is a deadline rather than a counter — there is nothing to
 * pause — and free thinking time on demand would be an exploit. Reading it costs
 * you seconds, exactly like every other action in a race.
 */

export function HelpButton({ onClick, title = 'Rules' }: { onClick: () => void; title?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={title}
      title={title}
      style={{
        width: 'clamp(24px,4.4cqmin,34px)',
        height: 'clamp(24px,4.4cqmin,34px)',
        flexShrink: 0,
        borderRadius: '50%',
        border: '1px solid rgba(166,255,46,.35)',
        background: 'rgba(166,255,46,.07)',
        color: color.lime,
        cursor: 'pointer',
        display: 'grid',
        placeItems: 'center',
        padding: 0,
      }}
    >
      <Icon name="help" size="58%" />
    </button>
  );
}

export function HelpDialog({
  minigameId,
  onClose,
}: {
  minigameId: MinigameId;
  onClose: () => void;
}) {
  // Escape closes. A player reaching for the keyboard mid-race should not have
  // to hunt for a button.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const meta = MINIGAME_META[minigameId];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${meta.title} rules`}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 40,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(4,7,4,.78)',
        backdropFilter: 'blur(3px)',
        padding: 20,
        animation: 'rhBannerIn .18s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(460px,100%)',
          // A landscape phone is ~360px tall. Cap the dialog and let it scroll
          // internally rather than run off the top and bottom of the viewport.
          maxHeight: 'min(560px,100%)',
          overflowY: 'auto',
          border: '1px solid rgba(166,255,46,.28)',
          borderRadius: 14,
          background: 'linear-gradient(180deg,rgba(16,22,14,.98),rgba(10,14,9,.98))',
          padding: 'clamp(16px,3cqmin,26px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
          boxShadow: '0 24px 70px rgba(0,0,0,.6)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontWeight: 800, fontSize: 20, letterSpacing: '.06em', color: color.lime }}>
            {meta.title}
          </span>
          <span style={{ fontSize: 10, letterSpacing: '.16em', color: 'rgba(234,255,234,.4)' }}>
            {meta.subtitle}
          </span>
        </div>

        <p style={{ margin: 0, fontSize: 15, lineHeight: 1.45, color: '#eaffea' }}>{meta.goal}</p>

        <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 7 }}>
          {meta.rules.map((rule) => (
            <li key={rule} style={{ fontSize: 13.5, lineHeight: 1.45, color: 'rgba(234,255,234,.76)' }}>
              {rule}
            </li>
          ))}
        </ul>

        <div
          style={{
            marginTop: 2,
            paddingTop: 12,
            borderTop: '1px solid rgba(166,255,46,.12)',
            fontSize: 11.5,
            color: 'rgba(255,176,32,.75)',
          }}
        >
          The clock keeps running while this is open.
        </div>

        <button
          type="button"
          onClick={onClose}
          style={{
            alignSelf: 'flex-end',
            border: '1px solid rgba(166,255,46,.4)',
            borderRadius: 8,
            background: 'rgba(166,255,46,.1)',
            color: color.lime,
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: '.06em',
            padding: '8px 18px',
            cursor: 'pointer',
          }}
        >
          GOT IT
        </button>
      </div>
    </div>
  );
}
